import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  FileText, 
  Layers, 
  PackageCheck, 
  Box, 
  Truck,
  CheckCircle2,
  XCircle,
  Mail
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const ShippingInstructionsPage = () => {
  const packingSteps = [
    {
      number: 1,
      icon: FileText,
      title: "Print your packing slip",
      description: "Include your submission number clearly visible"
    },
    {
      number: 2,
      icon: Layers,
      title: "Sleeve each card",
      description: "Place cards in penny sleeves, then in card savers or top loaders"
    },
    {
      number: 3,
      icon: PackageCheck,
      title: "Bundle securely",
      description: "Stack cards together and wrap with rubber band or tape"
    },
    {
      number: 4,
      icon: Box,
      title: "Box and pad",
      description: "Place bundle in box with bubble wrap on all sides"
    },
    {
      number: 5,
      icon: Truck,
      title: "Ship with tracking",
      description: "Use USPS Priority, UPS, or FedEx with insurance"
    }
  ];

  const dos = [
    "Use card savers or top loaders for each card",
    "Wrap cards in bubble wrap or foam padding",
    "Use a sturdy cardboard box with minimal empty space",
    "Include your packing slip with submission number",
    "Use tracked and insured shipping",
    "Seal the box securely with packing tape"
  ];

  const donts = [
    "Do not use soft penny sleeves alone",
    "Do not use rubber bands on cards",
    "Do not ship in envelopes",
    "Do not leave cards loose in the box",
    "Do not use newspaper as padding",
    "Do not forget your submission number"
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Link */}
          <Link 
            to="/dashboard" 
            className="inline-flex items-center text-gray-600 hover:text-[#003594] mb-6 transition-colors"
            data-testid="back-to-dashboard-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>

          {/* Page Header */}
          <div className="flex items-start gap-4 mb-8">
            <div className="w-14 h-14 bg-[#003594] rounded-xl flex items-center justify-center flex-shrink-0">
              <Package className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="shipping-instructions-title">
                Shipping Instructions
              </h1>
              <p className="text-gray-600 mt-1">
                How to safely ship your cards to NICE Grading
              </p>
            </div>
          </div>

        {/* Ship To Section - Dual Addresses */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm" data-testid="ship-to-section">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Package className="w-4 h-4 text-[#003594]" />
            </div>
            Ship To
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose the address that matches your shipping carrier:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* USPS Address */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-testid="usps-address">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-blue-800">USPS</span>
              </div>
              <div className="text-gray-700 text-sm">
                <p className="font-semibold">NICE Grading</p>
                <p>Attn: Receiving</p>
                <p>P.O. Box 30041</p>
                <p>Rochester, NY 14603</p>
              </div>
            </div>
            
            {/* UPS/FedEx Address */}
            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4" data-testid="ups-fedex-address">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                  <Truck className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-amber-800">UPS / FedEx</span>
              </div>
              <div className="text-gray-700 text-sm">
                <p className="font-semibold">NICE Grading</p>
                <p>Attn: Receiving</p>
                <p>250 Cumberland St.</p>
                <p>Ste. 202</p>
                <p>Rochester, NY 14605</p>
              </div>
            </div>
          </div>
        </div>

        {/* Packing Steps Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm" data-testid="packing-steps-section">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Packing Steps</h2>
          
          <div className="space-y-4">
            {packingSteps.map((step) => {
              const IconComponent = step.icon;
              return (
                <div key={step.number} className="flex items-start gap-4" data-testid={`packing-step-${step.number}`}>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center font-semibold text-sm">
                      {step.number}
                    </div>
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div className="pt-1">
                    <h3 className="font-semibold text-gray-900">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Do's and Don'ts Section */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {/* Do's */}
          <div className="bg-blue-50 rounded-xl border border-blue-100 p-6" data-testid="dos-section">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
              Best Practices
            </h3>
            <ul className="space-y-3">
              {dos.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Don'ts */}
          <div className="bg-red-50 rounded-xl border border-red-100 p-6" data-testid="donts-section">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Avoid These
            </h3>
            <ul className="space-y-3">
              {donts.map((item, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Additional Tips */}
        <div className="bg-gradient-to-r from-[#003594] to-blue-600 rounded-xl p-6 text-white">
          <h3 className="font-semibold mb-2">Ready to ship?</h3>
          <p className="text-blue-100 text-sm mb-4">
            Make sure you have included your packing slip with your submission number visible. 
            This helps us process your cards quickly and accurately.
          </p>
          <Link 
            to="/dashboard"
            className="inline-flex items-center gap-2 bg-white text-[#003594] px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            data-testid="view-submissions-link"
          >
            View Your Submissions
            <ArrowLeft className="w-4 h-4 rotate-180" />
          </Link>
        </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingInstructionsPage;
